import { z } from 'zod';

// The feature registry (docs/10) — the single source of truth for what *can*
// be toggled per concept. Consumed by nav, route guards, API checks and the
// admin config UI. `concept_features` rows override defaults; absent row =
// default. Core pages (dashboard, garage, staff, account, auth) are not here
// and can never be disabled.

export const FEATURE_KEYS = ['news', 'announcements', 'downloads', 'benefits', 'events', 'garage_events', 'rewards', 'fleet'] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];
export const featureKeyEnum = z.enum(FEATURE_KEYS);

export interface FeatureDefinition {
  key: FeatureKey;
  defaultEnabled: boolean;
  /** Disabling the parent disables this feature in effect (docs/10). */
  requires?: FeatureKey;
}

export const FEATURE_REGISTRY: FeatureDefinition[] = [
  { key: 'news', defaultEnabled: true },
  { key: 'announcements', defaultEnabled: true, requires: 'news' },
  { key: 'downloads', defaultEnabled: true },
  { key: 'benefits', defaultEnabled: true },
  { key: 'events', defaultEnabled: true },
  { key: 'garage_events', defaultEnabled: true, requires: 'events' },
  // Loyalty (points & rewards) — opt-in per concept; not every concept runs a
  // spend-based scheme, so it is off by default and enabled per concept.
  { key: 'rewards', defaultEnabled: false },
  // Fleet programme — opt-in per concept; only concepts that operate a fleet
  // network (like 1TEC) run it, so off by default and enabled per concept.
  { key: 'fleet', defaultEnabled: false },
];

export type FeatureMap = Record<FeatureKey, boolean>;

/**
 * Effective state = override if present, else registry default, with the
 * dependency cascade applied (parent off → child off in effect; stored
 * overrides preserved so re-enabling the parent restores child state).
 * The ONE resolution implementation — backend and frontend both use it.
 */
export function resolveEffectiveFeatures(overrides: Partial<Record<FeatureKey, boolean>>): FeatureMap {
  const base = {} as FeatureMap;
  for (const def of FEATURE_REGISTRY) {
    base[def.key] = overrides[def.key] ?? def.defaultEnabled;
  }
  for (const def of FEATURE_REGISTRY) {
    if (def.requires && !base[def.requires]) base[def.key] = false;
  }
  return base;
}
